import { useState } from "react";
import { useTenantMutation } from "../hooks/useTenantData";
import type { ImportCommitResult, ImportPreview } from "../api/types";
import { InlineError, PageHeader, Panel, Stat } from "../components/ui";

export function ImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [report, setReport] = useState<ImportCommitResult | null>(null);

  const runPreview = useTenantMutation<File, ImportPreview>(
    (api, t, f) => api.previewImport(t, f),
    [],
  );
  const commit = useTenantMutation<string, ImportCommitResult>(
    (api, t, importId) => api.commitImport(t, importId),
    [["blocks"], ["units"], ["people"], ["occupancies"], ["audit-events"]],
  );

  function validate() {
    if (!file) return;
    setReport(null);
    runPreview.mutate(file, { onSuccess: setPreview });
  }

  function confirm() {
    if (!preview) return;
    commit.mutate(preview.import_id, {
      onSuccess: (result) => {
        setReport(result);
        setPreview(null);
        setFile(null);
      },
    });
  }

  return (
    <>
      <PageHeader
        title="Importação"
        subtitle="Cadastro em lote de blocos, unidades e pessoas via planilha CSV ou XLSX."
      />
      <Panel title="1 · Enviar planilha">
        <div className="upload">
          <label>
            Arquivo (.csv ou .xlsx)
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setPreview(null);
                setReport(null);
              }}
            />
          </label>
          <div className="actions">
            <button type="button" disabled={!file || runPreview.isPending} onClick={validate}>
              {runPreview.isPending ? "Validando…" : "Validar planilha"}
            </button>
          </div>
          <InlineError error={runPreview.error} />
        </div>
      </Panel>
      {preview && (
        <Panel title="2 · Pré-visualização">
          <div className="import-summary">
            <Stat label="Linhas" value={preview.total_rows} />
            <Stat label="Válidas" value={preview.valid_rows} />
            <Stat label="Inválidas" value={preview.invalid_rows} />
          </div>
          {preview.errors.length > 0 && (
            <>
              <p className="error" role="alert">
                Corrija as linhas abaixo na planilha e valide novamente. Nada foi gravado.
              </p>
              <div className="table">
                {preview.errors.map((error) => (
                  <div className="table-row" key={`${error.row}-${error.field}`}>
                    <span>
                      <strong>Linha {error.row} · campo “{error.field}”</strong>
                      <small>{error.message}</small>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
          {preview.invalid_rows === 0 && (
            <p className="success">Todas as linhas são válidas. Nada foi gravado ainda — confirme para importar.</p>
          )}
          <div className="actions" style={{ marginTop: 14 }}>
            <button
              type="button"
              disabled={preview.invalid_rows > 0 || commit.isPending}
              onClick={confirm}
            >
              {commit.isPending ? "Importando…" : "Confirmar importação"}
            </button>
          </div>
          <InlineError error={commit.error} />
        </Panel>
      )}
      {report && (
        <Panel title="3 · Relatório final">
          <p className="success">Importação concluída.</p>
          <ul>
            {Object.entries(report.created).map(([entity, count]) => (
              <li key={entity}>
                {count} {entity} criado(s)
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}
